import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Link as LinkIcon, Mail, MessageCircle, RefreshCw, Send, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../services/analytics';
import { buildLocalizedHomepageShareUrl } from '../utils/shareLinks';

type ShareChannel = 'native' | 'whatsapp' | 'telegram' | 'x' | 'email' | 'copy';
type ShareSurface = 'header' | 'chat_drawer' | 'answer_menu';

type ShareWithFriendsProps = {
  className?: string;
  variant?: 'header' | 'drawer' | 'floating';
  sourceSurface?: ShareSurface;
  openEventName?: string;
};

const defaultMessages = [
  "ok this is a bit niche, but you might like it - sarvalok.com pulls from Sanskrit texts for Dharma and Jyotisha questions.",
  "came across Sarvalok today. If you ever ask Hinduism / Jyotisha questions, this one feels more serious than the usual AI answers.",
  "sharing this because it is oddly useful: Sarvalok answers from Indian knowledge texts instead of random internet summaries.",
  "you might appreciate this - Sarvalok has Heritage chat, Jyotisha, audio scripture mode, citations, and source reading in one place.",
];

const isLikelyMobileShareTarget = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768 || window.matchMedia?.('(pointer: coarse)').matches;
};

const openCenteredPopup = (url: string) => {
  if (typeof window === 'undefined') return;
  window.open(url, '_blank', 'noopener,noreferrer,width=760,height=640');
};

const ShareWithFriends: React.FC<ShareWithFriendsProps> = ({
  className = '',
  openEventName = 'sarvalok_open_share',
  sourceSurface = 'header',
  variant = 'header',
}) => {
  const { t, i18n } = useTranslation('chat');
  const [isOpen, setIsOpen] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const [customMessage, setCustomMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const messages = useMemo(() => {
    const translated = t('chat.share_friend.messages', { returnObjects: true }) as unknown;
    return Array.isArray(translated) && translated.every((item) => typeof item === 'string')
      ? translated as string[]
      : defaultMessages;
  }, [t]);

  const templateMessage = messages[messageIndex % messages.length] || defaultMessages[0];
  const message = customMessage ?? templateMessage;

  const shareUrlFor = useCallback((channel: ShareChannel) => buildLocalizedHomepageShareUrl(channel, i18n.language), [i18n.language]);

  const recordShare = useCallback((method: ShareChannel, successState: 'attempted' | 'completed' | 'cancelled' | 'failed' = 'attempted') => {
    trackEvent('share_click', {
      category: 'Sharing',
      method,
      source_surface: sourceSurface,
      shared_object: 'site',
      ui_language: i18n.language,
      device_type: typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop',
      success_state: successState,
      utm_campaign: 'friend_share',
    });
  }, [i18n.language, sourceSurface]);

  const nativeShare = useCallback(async () => {
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
    const url = shareUrlFor('native');
    try {
      recordShare('native', 'attempted');
      await navigator.share({
        title: t('chat.share_friend.native_title', 'Sarvalok'),
        text: message,
        url,
      });
      recordShare('native', 'completed');
      return true;
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        recordShare('native', 'cancelled');
        return true;
      }
      recordShare('native', 'failed');
      return false;
    }
  }, [message, recordShare, shareUrlFor, t]);

  const handleTrigger = async () => {
    if (isLikelyMobileShareTarget()) {
      const handled = await nativeShare();
      if (handled) return;
    }
    setIsOpen((current) => !current);
  };

  useEffect(() => {
    const handleOpenShare = async () => {
      if (isLikelyMobileShareTarget()) {
        const handled = await nativeShare();
        if (handled) return;
      }
      setIsOpen(true);
    };
    window.addEventListener(openEventName, handleOpenShare);
    return () => window.removeEventListener(openEventName, handleOpenShare);
  }, [nativeShare, openEventName]);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setIsOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const shareTo = async (channel: Exclude<ShareChannel, 'native'>) => {
    const url = shareUrlFor(channel);
    const encodedText = encodeURIComponent(message);
    const encodedUrl = encodeURIComponent(url);
    recordShare(channel, 'attempted');

    if (channel === 'copy') {
      try {
        await navigator.clipboard.writeText(`${message}\n${url}`);
        setCopied(true);
        recordShare('copy', 'completed');
        window.setTimeout(() => setCopied(false), 1600);
      } catch {
        recordShare('copy', 'failed');
      }
      return;
    }

    if (channel === 'whatsapp') {
      openCenteredPopup(`https://wa.me/?text=${encodeURIComponent(`${message}\n${url}`)}`);
    } else if (channel === 'telegram') {
      openCenteredPopup(`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`);
    } else if (channel === 'x') {
      openCenteredPopup(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`);
    } else if (channel === 'email') {
      window.location.href = `mailto:?subject=${encodeURIComponent(t('chat.share_friend.email_subject', 'Thought you might like Sarvalok'))}&body=${encodeURIComponent(`${message}\n\n${url}`)}`;
    }
  };

  const triggerLabel = t('chat.share_friend.trigger', 'Tell a friend');
  const triggerClassName = variant === 'drawer'
    ? 'sl-share-trigger flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-800'
    : variant === 'floating'
      ? 'sl-share-trigger inline-flex h-7 items-center justify-center gap-1.5 rounded-full border border-amber-200/80 bg-white px-3 text-xs font-medium leading-none text-slate-700 shadow-[0_10px_28px_-14px_rgba(15,23,42,0.45)] ring-1 ring-slate-950/[0.02] transition-colors hover:bg-amber-50 hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:border-amber-500/25 dark:bg-slate-950 dark:text-slate-200 dark:ring-white/[0.04] dark:hover:bg-amber-500/10 dark:hover:text-white dark:focus-visible:ring-offset-slate-950'
      : 'sl-share-trigger hidden md:inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium leading-none text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800';
  const rootClassName = variant === 'floating'
    ? `sl-share-popover ${className}`
    : `sl-share-popover relative ${className}`;

  return (
    <div ref={rootRef} className={rootClassName}>
      <button
        type="button"
        onClick={handleTrigger}
        className={triggerClassName}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={triggerLabel}
        title={variant === 'floating' ? triggerLabel : undefined}
      >
        <MessageCircle className="h-4 w-4" />
        <span className={variant === 'floating' ? 'whitespace-nowrap' : undefined}>{triggerLabel}</span>
      </button>

      {isOpen ? (
        <div className="absolute right-0 top-[calc(100%+0.5rem)] z-[1000] w-[min(26rem,calc(100vw-2rem))] rounded-lg border border-slate-200 bg-white p-4 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-950 dark:text-slate-100">
                {t('chat.share_friend.title', 'Tell a friend about Sarvalok')}
              </div>
              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {t('chat.share_friend.subtitle', 'A pre-written note, kept casual.')}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              aria-label={t('chat.share_friend.close', 'Close')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-4 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm leading-6 text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
            <div className="flex items-start gap-3">
              <textarea
                value={message}
                onChange={(event) => setCustomMessage(event.target.value)}
                rows={4}
                className="min-w-0 flex-1 resize-none border-0 bg-transparent p-0 text-sm leading-6 text-slate-700 outline-none placeholder:text-slate-400 focus:ring-0 dark:text-slate-200 dark:placeholder:text-slate-500"
                aria-label={t('chat.share_friend.message_label', 'Share message')}
              />
              <button
                type="button"
                onClick={() => {
                  setMessageIndex((current) => current + 1);
                  setCustomMessage(null);
                }}
                className="rounded-md p-1 text-slate-400 hover:bg-white hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                aria-label={t('chat.share_friend.refresh', 'Try another note')}
                title={t('chat.share_friend.refresh', 'Try another note')}
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-5 gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">
            <button type="button" onClick={() => void shareTo('whatsapp')} className="sl-share-channel flex flex-col items-center gap-1 rounded-md p-2 text-xs text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800">
              <MessageCircle className="h-5 w-5 text-emerald-500" />
              WhatsApp
            </button>
            <button type="button" onClick={() => void shareTo('telegram')} className="sl-share-channel flex flex-col items-center gap-1 rounded-md p-2 text-xs text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800">
              <Send className="h-5 w-5 text-sky-500" />
              Telegram
            </button>
            <button type="button" onClick={() => void shareTo('x')} className="sl-share-channel flex flex-col items-center gap-1 rounded-md p-2 text-xs text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800">
              <span className="text-lg font-semibold leading-5 text-slate-950 dark:text-slate-100">X</span>
              X
            </button>
            <button type="button" onClick={() => void shareTo('email')} className="sl-share-channel flex flex-col items-center gap-1 rounded-md p-2 text-xs text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800">
              <Mail className="h-5 w-5 text-slate-500 dark:text-slate-300" />
              Email
            </button>
            <button type="button" onClick={() => void shareTo('copy')} className="sl-share-channel flex flex-col items-center gap-1 rounded-md p-2 text-xs text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800">
              {copied ? <Check className="h-5 w-5 text-emerald-500" /> : <LinkIcon className="h-5 w-5 text-slate-500 dark:text-slate-300" />}
              {copied ? t('chat.share_friend.copied', 'Copied') : t('chat.share_friend.copy', 'Copy')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ShareWithFriends;
